import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import VictoryModal from '../../components/game/VictoryModal';
import HowToPlayModal from '../../components/game/HowToPlayModal';
import { useGameProgress } from '../../hooks/useGameProgress';
import { useSessionContext } from '../../hooks/useSessionContext';
import SessionBanner from '../../components/game/SessionBanner';

type Direction = 'UP' | 'RIGHT' | 'DOWN' | 'LEFT';

interface Cell {
  r: number;
  c: number;
  regionId: number;
  type: 'arrow' | 'target';
  direction: Direction | null;
  solutionDirection: Direction | null;
  isClue: boolean;
  hasError?: boolean;
}

interface ConvergenciaLevel {
  size: number;
  grid: Cell[][];
  targets: { r: number; c: number }[];
  pistas: { r: number; c: number }[];
  regionCount: number;
  seed: number;
}

export default function ConvergenciaGame() {
  const { dificultad } = useParams<{ dificultad: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [level, setLevel] = useState<ConvergenciaLevel | null>(null);
  const [userBoard, setUserBoard] = useState<Cell[][] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [startTime] = useState(Date.now());
  const [moves, setMoves] = useState(0);
  const [solved, setSolved] = useState(false);
  const [showVictory, setShowVictory] = useState(false);
  const [undoStack, setUndoStack] = useState<Cell[][][]>([]);
  const [redoStack, setRedoStack] = useState<Cell[][][]>([]);
  const [modoAsistido, setModoAsistido] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);

  const dificultadOrden = parseInt(dificultad || '1');
  const { saveProgress: saveGameProgress } = useGameProgress('convergencia', dificultadOrden);

  const boardAreaRef = useRef<HTMLDivElement>(null);
  const [boardW, setBoardW] = useState(0);
  const [boardH, setBoardH] = useState(0);

  const measureBoard = () => {
    if (boardAreaRef.current) {
      const { clientWidth, clientHeight } = boardAreaRef.current;
      setBoardW(clientWidth - 16);
      setBoardH(clientHeight - 16);
    }
  };

  useEffect(() => {
    if (!loading) requestAnimationFrame(measureBoard);
  }, [loading]);

  useEffect(() => {
    window.addEventListener('resize', measureBoard);
    return () => window.removeEventListener('resize', measureBoard);
  }, []);
  const { isInSession, sessionType, saveSessionResult } = useSessionContext();

  useEffect(() => {
    loadPuzzle();
  }, [dificultadOrden]);

  const loadPuzzle = async (forceNew = false) => {
    try {
      setLoading(true);
      setError('');

      const urlSeed = !forceNew ? searchParams.get('seed') : null;
      const seedToUse = urlSeed ? parseInt(urlSeed) : undefined;

      const response = await api.get('/convergencia/generate', {
        params: {
          dificultad: dificultadOrden,
          ...(seedToUse && { seed: seedToUse })
        }
      });

      const data: ConvergenciaLevel = response.data.data;
      setLevel(data);
      setUserBoard(JSON.parse(JSON.stringify(data.grid)));
      setMoves(0);
      setSolved(false);
      setShowVictory(false);
      setUndoStack([]);
      setRedoStack([]);

      setSearchParams({ seed: data.seed.toString() }, { replace: true });
    } catch (err: any) {
      console.error('Error al cargar puzzle:', err);
      setError(err.response?.data?.error || 'Error al cargar el puzzle');
    } finally {
      setLoading(false);
    }
  };

  const handleCellClick = (r: number, c: number) => {
    if (!userBoard || solved) return;

    const cell = userBoard[r][c];
    if (cell.type === 'target' || cell.isClue) return;

    // Guardar para undo
    setUndoStack([...undoStack, JSON.parse(JSON.stringify(userBoard))]);
    setRedoStack([]);

    // Ciclar direcciones
    const cycle: (Direction | null)[] = [null, 'UP', 'RIGHT', 'DOWN', 'LEFT'];
    const currentIndex = cycle.indexOf(cell.direction);
    const nextIndex = (currentIndex + 1) % cycle.length;

    const newBoard = userBoard.map(row => row.map(c => ({ ...c, hasError: false })));
    newBoard[r][c].direction = cycle[nextIndex];

    setUserBoard(newBoard);
    setMoves(moves + 1);

    // Verificar si está completo
    if (isComplete(newBoard)) {
      setTimeout(() => checkSolution(newBoard), 300);
    }
  };

  const isComplete = (board: Cell[][]): boolean => {
    for (const row of board) {
      for (const cell of row) {
        if (cell.type !== 'target' && cell.direction === null) {
          return false;
        }
      }
    }
    return true;
  };

  const checkSolution = async (board: Cell[][]) => {
    if (!level) return;

    try {
      const response = await api.post('/convergencia/validate', {
        board,
        solution: level.grid
      });

      const { valid, errors, message } = response.data.data;

      if (valid) {
        setSolved(true);
        const tiempo = Math.floor((Date.now() - startTime) / 1000);
        await saveProgress(tiempo);
        setShowVictory(true);
      } else {
        // Marcar errores
        if (errors && errors.length > 0) {
          const newBoard = board.map(row => row.map(c => ({ ...c })));
          errors.forEach((err: { r: number; c: number }) => {
            if (newBoard[err.r] && newBoard[err.r][err.c]) {
              newBoard[err.r][err.c].hasError = true;
            }
          });
          setUserBoard(newBoard);
        }
        setError(message || 'Hay errores en la solución');
        setTimeout(() => setError(''), 3000);
      }
    } catch (err: any) {
      console.error('Error validando:', err);
      setError('Error al validar la solución');
    }
  };

  const saveProgress = async (tiempo: number) => {
    try {
      // Guardar progreso normal (siempre)
      await saveGameProgress({
        tablero_id: level?.seed || 0,
        tiempo,
        movimientos: moves,
        completado: true,
      });

      // Si viene de una sesión, guardar también en la sesión
      await saveSessionResult({
        tiempo,
        movimientos: moves,
        completado: true,
        datos_juego: {
          tablero_id: level?.seed || 0,
          size: level?.size || 0,
        },
      });
    } catch (error) {
      console.error('Error guardando progreso:', error);
    }
  };

  const handleUndo = () => {
    if (undoStack.length === 0 || !userBoard) return;
    const prev = undoStack[undoStack.length - 1];
    setRedoStack([...redoStack, JSON.parse(JSON.stringify(userBoard))]);
    setUserBoard(prev);
    setUndoStack(undoStack.slice(0, -1));
  };

  const handleRedo = () => {
    if (redoStack.length === 0 || !userBoard) return;
    const next = redoStack[redoStack.length - 1];
    setUndoStack([...undoStack, JSON.parse(JSON.stringify(userBoard))]);
    setUserBoard(next);
    setRedoStack(redoStack.slice(0, -1));
  };

  const handleHint = async () => {
    if (!userBoard || !level) return;

    try {
      const response = await api.post('/convergencia/hint', {
        board: userBoard,
        solution: level.grid
      });

      const { hint } = response.data.data;
      if (hint) {
        setUndoStack([...undoStack, JSON.parse(JSON.stringify(userBoard))]);
        const newBoard = userBoard.map(row => row.map(c => ({ ...c })));
        newBoard[hint.r][hint.c].direction = hint.direction;
        newBoard[hint.r][hint.c].isClue = true;
        setUserBoard(newBoard);
        setMoves(moves + 1);
      }
    } catch (err: any) {
      console.error('Error obteniendo pista:', err);
    }
  };

  const showSolution = () => {
    if (!level || !window.confirm('¿Estás seguro? Esto mostrará la solución completa.')) return;
    const solBoard = level.grid.map(row =>
      row.map(cell => ({
        ...cell,
        direction: cell.solutionDirection,
        isClue: true
      }))
    );
    setUserBoard(solBoard);
    setSolved(true);
  };

  const handleReset = () => {
    if (!level || !window.confirm('¿Estás seguro de que quieres reiniciar?')) return;
    setUserBoard(JSON.parse(JSON.stringify(level.grid)));
    setMoves(0);
    setSolved(false);
    setShowVictory(false);
    setUndoStack([]);
    setRedoStack([]);
  };

  // ─── Modo asistido ────────────────────────────────────────────────────────
  function calcularErrores(board: Cell[][]): Set<string> {
    const errores = new Set<string>();
    const size = board.length;
    const deltas: Record<Direction, [number, number]> = {
      UP: [-1, 0], DOWN: [1, 0], LEFT: [0, -1], RIGHT: [0, 1]
    };

    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        const cell = board[r][c];
        if (cell.type === 'target' || !cell.direction) continue;

        const chain: string[] = [];
        const visited = new Set<string>();
        let cr = r, cc = c;
        let definiteError = false;

        while (true) {
          const key = `${cr},${cc}`;
          if (visited.has(key)) { definiteError = true; break; } // bucle
          visited.add(key);
          chain.push(key);

          const cur = board[cr][cc];
          if (cur.type === 'target') break; // llega al objetivo ✓
          if (!cur.direction) break;        // incompleta, no es error aún

          const [dr, dc] = deltas[cur.direction];
          const nr = cr + dr, nc = cc + dc;
          if (nr < 0 || nr >= size || nc < 0 || nc >= size) { definiteError = true; break; } // sale del tablero
          cr = nr; cc = nc;
        }

        if (definiteError) chain.forEach(k => errores.add(k));
      }
    }

    // Regla 2: no dos flechas con la misma dirección en la misma región
    const regionDirs = new Map<number, Map<Direction, string>>();
    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        const cell = board[r][c];
        if (cell.type === 'target' || !cell.direction) continue;
        const rid = cell.regionId;
        if (!regionDirs.has(rid)) regionDirs.set(rid, new Map());
        const dirMap = regionDirs.get(rid)!;
        const key = `${r},${c}`;
        if (dirMap.has(cell.direction)) {
          errores.add(dirMap.get(cell.direction)!);
          errores.add(key);
        } else {
          dirMap.set(cell.direction, key);
        }
      }
    }

    return errores;
  }

  const getSymbol = (cell: Cell): string => {
    if (cell.type === 'target') return '●';

    const dir = cell.direction;
    if (!dir) return '';

    const map: Record<Direction, string> = {
      'UP': '↑',
      'DOWN': '↓',
      'LEFT': '←',
      'RIGHT': '→'
    };

    return map[dir] || '';
  };

  // Colores de región como hex (inline styles para evitar purge de Tailwind en producción)
  const REGION_COLORS = [
    '#bfdbfe', '#bbf7d0', '#fce7f3', '#ddd6fe',
    '#99f6e4', '#fed7aa', '#fecaca', '#fef08a',
    '#a5f3fc', '#d9f99d', '#e9d5ff', '#fdba74',
  ];

  const getCellStyle = (r: number, c: number, cell: Cell, hasError: boolean): React.CSSProperties => {
    const size = userBoard!.length;
    const currentRegion = cell.regionId;

    // Bordes de región — solo derecho y abajo para evitar bordes dobles
    const regionBorder = '6px solid #000000';
    const thinBorder   = '1px solid #e5e7eb';
    const border: React.CSSProperties = {
      borderTop:    r === 0       ? regionBorder : thinBorder,
      borderLeft:   c === 0       ? regionBorder : thinBorder,
      borderRight:  c < size-1 && userBoard![r][c+1].regionId !== currentRegion ? regionBorder
                  : c === size-1  ? regionBorder : thinBorder,
      borderBottom: r < size-1 && userBoard![r+1][c].regionId !== currentRegion ? regionBorder
                  : r === size-1  ? regionBorder : thinBorder,
    };

    // Color de fondo y texto según origen (sistema vs usuario)
    let backgroundColor: string;
    let color: string;

    if (cell.type === 'target') {
      backgroundColor = '#1f2937';
      color = '#ffffff';
    } else if (hasError) {
      backgroundColor = '#fee2e2';
      color = '#dc2626';
    } else if (cell.isClue) {
      backgroundColor = '#ffffff';
      color = '#1f2937'; // flecha dada por el sistema: negra, como impresa
    } else if (cell.direction) {
      backgroundColor = '#ffffff';
      color = '#2563eb'; // flecha puesta por el usuario: azul
    } else {
      backgroundColor = '#ffffff';
      color = '#1f2937';
    }

    return { width: cellW, height: cellH, fontSize, backgroundColor, color, ...border };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 to-indigo-900 flex items-center justify-center">
        <div className="text-white text-2xl">🔄 Cargando...</div>
      </div>
    );
  }

  if (!level || !userBoard) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 to-indigo-900 flex items-center justify-center">
        <div className="text-white text-2xl">❌ Error al cargar puzzle</div>
      </div>
    );
  }

  const size = level.size;
  const gap = 1;
  const pad = 4;
  const cellW = boardW > 0 ? Math.floor((boardW - pad * 2 - gap * (size - 1)) / size) : 60;
  const cellH = boardH > 0 ? Math.floor((boardH - pad * 2 - gap * (size - 1)) / size) : 60;
  const fontSize = Math.floor(Math.min(cellW, cellH) * 0.45);

  const difficulties = [
    { orden: 1, nombre: 'Neurona', icono: '🧠' },
    { orden: 2, nombre: 'Sinapsis', icono: '⚡' },
    { orden: 3, nombre: 'Cerebro', icono: '🧩' },
    { orden: 4, nombre: 'Genio', icono: '💡' },
    { orden: 5, nombre: 'Einstein', icono: '🎓' },
  ];

  const errores = modoAsistido ? calcularErrores(userBoard) : new Set<string>();

  return (
    <div className="h-full bg-gradient-to-br from-purple-900 to-indigo-900 flex flex-col">
      {/* Banner de Sesión */}
      {isInSession && sessionType && <SessionBanner sessionType={sessionType as 'presencial' | 'remota'} />}

      {/* Compact top bar */}
      <div className="bg-black/20 backdrop-blur-sm px-3 py-2 flex items-center gap-2 flex-wrap">
        <span className="text-white font-bold text-base whitespace-nowrap">🎯 Convergencia</span>
        <div className="w-px h-5 bg-white/30 hidden sm:block" />
        <div className="flex gap-1 flex-wrap">
          {difficulties.map(d => (
            <button
              key={d.orden}
              onClick={() => navigate(`/juegos/convergencia/${d.orden}`)}
              className={`px-2 py-1 rounded-md text-xs font-semibold transition-all ${
                dificultadOrden === d.orden ? 'bg-white text-purple-900' : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              {d.icono} <span className="hidden sm:inline">{d.nombre}</span>
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <div className="flex gap-1 items-center">
          <button onClick={handleUndo} disabled={undoStack.length === 0} title="Deshacer"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 7v6h6"/><path d="M21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13"/></svg>
          </button>
          <button onClick={handleRedo} disabled={redoStack.length === 0} title="Rehacer"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 7v6h-6"/><path d="M3 17a9 9 0 019-9 9 9 0 016 2.3L21 13"/></svg>
          </button>
          {/* Modo asistido */}
          <button onClick={() => setModoAsistido(v => !v)}
            title={modoAsistido ? 'Desactivar modo asistido' : 'Activar modo asistido'}
            className={`p-1.5 rounded-md transition-all text-white ${modoAsistido ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-white/20 hover:bg-white/30'}`}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </button>
          <button onClick={handleHint} disabled={solved} title="Pista"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18h6M10 22h4M12 2a7 7 0 017 7c0 2.38-1.19 4.47-3 5.74V17H8v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 017-7z"/></svg>
          </button>
          <button onClick={showSolution} disabled={solved} title="Ver solución"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
          </button>
          <button onClick={handleReset} title="Reiniciar"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 109-9M3 3v6h6"/></svg>
          </button>
          <button onClick={() => loadPuzzle(true)} title="Nuevo tablero"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M12 8v8M8 12h8"/></svg>
          </button>
          <button onClick={() => setShowHowToPlay(true)} title="Cómo se juega"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mx-3 mt-2 p-2 bg-red-100 border border-red-300 text-red-800 rounded-lg text-sm text-center">
          {error}
        </div>
      )}

      {/* Board area */}
      <div ref={boardAreaRef} className="flex-1 min-h-0 flex items-center justify-center p-2">
        <div
          className="convergencia-board grid bg-gray-300 rounded-lg"
          style={{
            gridTemplateColumns: `repeat(${size}, ${cellW}px)`,
            gridTemplateRows: `repeat(${size}, ${cellH}px)`,
            gap: `${gap}px`,
            padding: `${pad}px`,
          }}
        >
          {userBoard.map((row, r) =>
            row.map((cell, c) => {
              const symbol = getSymbol(cell);
              const isClickable = cell.type !== 'target' && !cell.isClue && !solved;
              const hasError = errores.has(`${r},${c}`) || cell.hasError;

              return (
                <button
                  key={`${r}-${c}`}
                  onClick={() => handleCellClick(r, c)}
                  disabled={!isClickable}
                  className={`flex items-center justify-center font-bold transition-all ${isClickable ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
                  style={getCellStyle(r, c, cell, hasError)}
                >
                  {symbol}
                </button>
              );
            })
          )}
        </div>
      </div>

      <HowToPlayModal
        isOpen={showHowToPlay}
        onClose={() => setShowHowToPlay(false)}
        gameTitle="Convergencia"
        gameIcon="🎯"
        objective="Coloca flechas en todas las casillas de forma que, siguiendo la cadena de flechas desde cualquier celda, siempre se llegue a una casilla marcada con un punto negro (●)."
        rules={[
          'Cada celda vacía debe tener una flecha apuntando en alguna dirección (↑ ↓ ← →)',
          'Siguiendo las flechas casilla a casilla, la cadena debe terminar en un punto negro (●)',
          'Dentro de cada región (área delimitada por bordes gruesos), no puede haber dos flechas apuntando en la misma dirección',
        ]}
        howToPlay="Toca o haz clic en una casilla para colocar una flecha. Toca repetidamente para cambiar su orientación: vacío → ↑ → → → ↓ → ← → vacío. Las casillas con pista (verde) no se pueden cambiar."
        author="Otto Janko"
      />

      {/* Victory Modal */}
      <VictoryModal
        isOpen={showVictory}
        onClose={() => setShowVictory(false)}
        stats={{
          tiempo: Math.floor((Date.now() - startTime) / 1000),
          movimientos: moves,
        }}
      />
    </div>
  );
}
